
import React from 'react';
import { DerivativeItem } from './types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Eye, Edit, Trash } from 'lucide-react';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { Badge } from '@/components/ui/badge';
import DerivativeRow from './DerivativeRow';

interface DerivativeListProps {
  derivatives: DerivativeItem[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onView?: (id: string) => void;
  showEditButton?: boolean;
  displayAsList?: boolean;
}

const DerivativeList: React.FC<DerivativeListProps> = ({ 
  derivatives, 
  onEdit, 
  onDelete,
  onView,
  showEditButton = false,
  displayAsList = false
}) => {
  const { t } = useLanguage();

  const getStatusBadge = (status: "pending" | "approved" | "rejected") => {
    switch (status) {
      case "approved":
        return <Badge variant="secondary" className="bg-green-100 hover:bg-green-200 text-green-800 border-none">{t('derivatives.status.approved')}</Badge>;
      case "rejected":
        return <Badge variant="secondary" className="bg-red-100 hover:bg-red-200 text-red-800 border-none">{t('derivatives.status.rejected')}</Badge>;
      case "pending":
      default:
        return <Badge variant="secondary" className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border-none">{t('derivatives.status.pending')}</Badge>;
    }
  };

  if (derivatives.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-muted-foreground">
          <p>{t('derivatives.noDerivatives')}</p>
          <p className="mt-2 text-sm">{t('creator.createNewDerivativeSuggestion')}</p>
        </div>
      </div>
    );
  }

  if (displayAsList) {
    return (
      <div className="space-y-2">
        {derivatives.map((derivative) => (
          <DerivativeRow 
            key={derivative.id}
            derivative={derivative}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {derivatives.map((derivative) => (
        <Card key={derivative.id} className="overflow-hidden">
          <div className="relative">
            <AspectRatio ratio={derivative.aspectRatio || 16/9}>
              <img 
                src={derivative.thumbnailUrl || "https://via.placeholder.com/640x360?text=No+Thumbnail"} 
                alt={derivative.title} 
                className="w-full h-full object-cover"
              />
            </AspectRatio>
            <div className="absolute top-2 right-2">
              {getStatusBadge(derivative.status)}
            </div>
          </div>
          
          <CardContent className="space-y-3 p-4">
            <div>
              <h3 className="text-lg font-semibold truncate">{derivative.title}</h3>
              <p className="text-sm text-muted-foreground truncate">
                Original: {derivative.originalTitle || 'Unknown Clip'}
              </p>
            </div>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {derivative.status === "approved" && `Approved: ${derivative.approvedDate}`}
                {derivative.status === "rejected" && `Rejected: ${derivative.rejectedDate}`}
                {derivative.status === "pending" && `Submitted: ${derivative.submittedDate}`}
              </span>
              {derivative.expressionType && (
                <span className="bg-white text-primary px-2 py-1 rounded font-bold">#{derivative.expressionType}</span>
              )}
            </div>
            
            {derivative.status === "rejected" && derivative.reviewComment && (
              <div className="text-xs bg-red-50 p-2 rounded text-red-800">
                <span className="font-medium">Rejection reason:</span> {derivative.reviewComment}
              </div>
            )}
            
            <div className="flex justify-between pt-2">
              <div className="flex gap-2">
                {onView && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => onView(derivative.id)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                )}
                
                {showEditButton && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={() => onEdit(derivative.id)}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive">
                    <Trash className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('derivatives.deleteConfirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('derivatives.deleteConfirmMessage')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => onDelete(derivative.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t('common.delete')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DerivativeList;
